/**
 * Contacts Page - Professional layout with company info AND a contact form
 * that creates a lead in the unified admin Leads panel (source=contact_form).
 */
import { useEffect, useState } from 'react';
import {
  MapPin, Phone, Mail, Clock, Building, Instagram,
  Send, Loader2, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import SEOHead from '../components/common/SEOHead';
import PublicHeader from '../components/common/PublicHeader';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactosPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Por favor preencha nome, email e mensagem.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact-form`, form);
      setSubmitted(true);
      toast.success('Mensagem enviada! Entramos em contacto em breve.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Não foi possível enviar a mensagem. Tente novamente.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Contactos — IMOCODE"
        description="Entre em contacto com a IMOCODE. Suporte para agentes imobiliários, apoio comercial, parcerias e integrações. Resposta em 24 horas."
        path="/contactos"
      />
      <PublicHeader />

      {/* Content */}
      <main className="container mx-auto px-6 py-12 pt-28 max-w-5xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Contactos</h1>
              <p className="text-gray-500">Entre em contacto connosco</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Company Info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações da Empresa</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <p className="text-lg font-semibold text-gray-900 mb-1">Cardume Pioneiro</p>
                  <p className="text-gray-600">Serviços Digitais, Lda.</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Morada
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <p className="text-gray-700">Estrada de São Tiago, n.º 376</p>
                  <p className="text-gray-700">2415-543 LEIRIA</p>
                  <p className="text-gray-500 mt-2">Portugal</p>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  Telefone
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <a href="tel:+351925663754" className="text-xl font-semibold text-blue-600 hover:underline">
                    +351 925 663 754
                  </a>
                  <p className="text-gray-500 text-sm mt-1">(chamada para rede móvel nacional)</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-purple-600" />
                  Email
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 space-y-3">
                  <div>
                    <p className="text-gray-500 text-sm">Suporte</p>
                    <a href="mailto:suporte@imocode.app" className="text-blue-600 hover:underline font-medium">
                      suporte@imocode.app
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Comercial</p>
                    <a href="mailto:comercial@imocode.app" className="text-blue-600 hover:underline font-medium">
                      comercial@imocode.app
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  Horário
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Segunda a Sexta</span>
                      <span className="text-gray-900 font-medium">10:00 - 13:00 | 15:00 - 17:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sábados, Domingos e Feriados</span>
                      <span className="text-red-500 font-medium">Encerrado</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="mt-10 pt-10 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Send className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Envie-nos uma mensagem</h2>
                <p className="text-gray-500 text-sm">Respondemos normalmente em 24h</p>
              </div>
            </div>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-3" />
                <h3 className="text-xl font-semibold text-green-900 mb-1">Mensagem enviada!</h3>
                <p className="text-green-700 mb-4">A nossa equipa entrará em contacto consigo em breve.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium"
                  data-testid="contact-form-send-another"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="grid md:grid-cols-2 gap-4"
                data-testid="contact-form"
              >
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="O seu nome"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    data-testid="contact-form-name"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="exemplo@dominio.pt"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    data-testid="contact-form-email"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+351 ..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    data-testid="contact-form-phone"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assunto</label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="Sobre o que pretende falar?"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    data-testid="contact-form-subject"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem *</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Escreva aqui a sua mensagem..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                    data-testid="contact-form-message"
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-medium shadow-sm transition"
                    data-testid="contact-form-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        A enviar...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Enviar mensagem
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Social Media */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Siga-nos nas Redes Sociais</h3>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/imocode.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition shadow-lg"
              >
                <Instagram className="w-5 h-5" />
                <span className="font-medium">@imocode.app</span>
              </a>
            </div>
          </div>

          {/* Map placeholder */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Localização</h3>
            <div className="bg-gray-100 rounded-xl h-64 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Estrada de São Tiago, n.º 376</p>
                <p>2415-543 LEIRIA, Portugal</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactosPage;
